// Leitor de extrato bancário em OFX (1.x SGML e 2.x XML).
//
// No OFX 1.x as tags-folha não fecham (<TRNAMT>-150.00 e quebra de linha); no
// 2.x fecham (<TRNAMT>-150.00</TRNAMT>). Os agregados (<STMTTRN>…</STMTTRN>)
// fecham nos dois, então basta recortar cada STMTTRN e ler o texto que vem
// depois de cada tag-folha até o próximo '<' ou fim de linha.
//
// Devolve o mesmo formato do parseCsv, com `fitid` em cada transação e
// bankId/acctId da conta (BANKACCTFROM ou CCACCTFROM).

import { nomeBancoPorCodigo, parseData, parseValor } from './comum';

function decodificarEntidades(s) {
  return s
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&apos;/gi, "'")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&amp;/gi, '&');
}

// Valor da primeira ocorrência de <TAG> dentro do trecho.
function folha(trecho, tag) {
  const m = trecho.match(new RegExp(`<${tag}>([^<\\r\\n]*)`, 'i'));
  return m ? decodificarEntidades(m[1]).trim() : '';
}

function agregado(trecho, tag) {
  const m = trecho.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`, 'i'));
  return m ? m[1] : '';
}

export function ehOfx(texto) {
  return /<OFX>/i.test(String(texto ?? '')) || /^\s*OFXHEADER\s*:/i.test(String(texto ?? ''));
}

export function parseOfx(texto) {
  const bruto = String(texto ?? '');
  const inicio = bruto.search(/<OFX>/i);
  if (inicio < 0) {
    throw new Error('O arquivo não está no formato OFX.');
  }
  const corpo = bruto.slice(inicio);

  const contaAgg = agregado(corpo, 'BANKACCTFROM') || agregado(corpo, 'CCACCTFROM');
  const bankId = folha(contaAgg, 'BANKID') || null;
  const acctId = folha(contaAgg, 'ACCTID') || null;
  const org = folha(agregado(corpo, 'FI'), 'ORG');
  const banco = nomeBancoPorCodigo(bankId) || org || null;

  const lista = agregado(corpo, 'BANKTRANLIST');
  const dtStart = parseData(folha(lista, 'DTSTART'));
  const dtEnd = parseData(folha(lista, 'DTEND'));

  const transacoes = [];
  const re = /<STMTTRN>([\s\S]*?)<\/STMTTRN>/gi;
  let m;
  while ((m = re.exec(corpo)) !== null) {
    const t = m[1];
    const data = parseData(folha(t, 'DTPOSTED'));
    // TRNAMT segue o padrão americano (ponto decimal); alguns bancos brasileiros
    // mandam vírgula. Ponto sozinho é sempre decimal aqui.
    const valor = parseValor(folha(t, 'TRNAMT'), { milharComPonto: false });
    if (!data || !Number.isFinite(valor) || Math.abs(valor) < 0.005) continue;
    const memo = folha(t, 'MEMO');
    const nome = folha(t, 'NAME');
    let descricao = memo || nome;
    if (memo && nome && !memo.toLowerCase().includes(nome.toLowerCase())) descricao = `${memo} — ${nome}`;
    transacoes.push({
      data,
      descricao: descricao || 'Movimentação importada',
      valor: Math.round(valor * 100) / 100,
      documento: folha(t, 'CHECKNUM') || folha(t, 'REFNUM') || '',
      fitid: folha(t, 'FITID') || null,
    });
  }

  if (transacoes.length === 0) {
    throw new Error('Nenhuma movimentação válida foi encontrada no arquivo.');
  }

  const datas = transacoes.map(x => x.data).sort();
  return {
    formato: 'ofx',
    banco,
    bankId,
    acctId,
    periodoInicio: dtStart && dtStart <= datas[0] ? dtStart : datas[0],
    periodoFim: dtEnd && dtEnd >= datas[datas.length - 1] ? dtEnd : datas[datas.length - 1],
    transacoes,
  };
}

export default parseOfx;
